import { Flex, PageGridContainer } from "@govlaunch/web";
import { sortBy } from "lodash";
import { useEffect, useState } from "react";
import { getCompactor, ResponsiveGridLayout, useContainerWidth } from "react-grid-layout";
import useIsMobile from "~/lib/hooks/useIsMobile";
import { IBlockLayoutType, ILandingPageConfigBlock } from "~/types/types";
import BlockLayout from "./BlockLayout";
import { prepareGridData } from "./prepareGridData";

interface ILandingPageGridProps {
  blocks: ILandingPageConfigBlock[];
  allowOverlap?: boolean | null;
}

export default function LandingPageGrid({ blocks, allowOverlap }: ILandingPageGridProps) {
  const isMobile = useIsMobile();
  const [isLoaded, setIsLoaded] = useState(false);
  const { width, containerRef, mounted } = useContainerWidth({
    measureBeforeMount: false,
    initialWidth: 1224,
  });

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const lgGridData = prepareGridData(blocks || []);
  const sortByKeys = lgGridData
    .sort((a, b) => {
      return a.x - b.x;
    })
    .sort((a, b) => {
      if (a.x === b.x) {
        return a.y - b.y;
      }
      return 0;
    })
    .map((item) => {
      return item.i;
    });

  return (
    <PageGridContainer mt={isMobile ? "40px" : "30px"}>
      {/* @ts-expect-error - ref is not correctly typed */}
      <div ref={containerRef}>
        {!isMobile && mounted && (
          <ResponsiveGridLayout
            width={width}
            className="layout"
            layouts={{
              lg: lgGridData,
            }}
            breakpoints={{
              lg: 1200,
              md: 960,
              sm: 640,
              xs: 480,
              xxs: 240,
            }}
            cols={{
              lg: 16,
              md: 16,
              sm: 16,
              xs: 16,
              xxs: 16,
            }}
            css={{
              opacity: isLoaded ? 1 : 0,
              transition: "opacity 0.3s ease-in-out",
            }}
            rowHeight={8} // px
            margin={[8, 8]}
            resizeConfig={{
              enabled: false,
            }}
            dragConfig={{
              enabled: false,
            }}
            compactor={getCompactor("vertical", allowOverlap || false)}
          >
            {sortBy(blocks || [], (block) => {
              return sortByKeys.indexOf(block.i);
            }).map((block) => (
              <div key={block.i}>
                <BlockLayout block={block} />
              </div>
            ))}
          </ResponsiveGridLayout>
        )}
      </div>
      {isMobile && (
        <Flex direction="column" alignItems="center" width="100%" mb="20px">
          {sortBy(bringStatCountersOnTop(blocks || []), (block) => {
            return sortByKeys.indexOf(block.i);
          }).map((block) => (
            <div
              key={block.i}
              css={{
                width: "100%",
                "&:not(:first-of-type)": {
                  marginTop: "40px",
                },
              }}
            >
              <BlockLayout block={block} />
            </div>
          ))}
        </Flex>
      )}
    </PageGridContainer>
  );
}

function bringStatCountersOnTop(blocks: ILandingPageConfigBlock[]) {
  return [...blocks].sort((block1, block2) => {
    if (
      block1.blockData.type !== IBlockLayoutType.StatCounters &&
      block2.blockData.type === IBlockLayoutType.StatCounters
    ) {
      return 1;
    }

    if (
      block1.blockData.type === IBlockLayoutType.StatCounters &&
      block2.blockData.type !== IBlockLayoutType.StatCounters
    ) {
      return -1;
    }

    return 0;
  });
}
